import { isNetworkError, type ApiEnvelope } from '@/api/errors';
import { filenameFromContentDisposition, triggerBlobDownload } from '@/lib/download';
import type {
  AgentInfo,
  AllDocuments,
  BankAccount,
  BankInfo,
  CompanyCard,
  CompanyCardSave,
  ContactPhone,
  DocType,
  DocumentGroup,
  TaxSystem,
} from '@/types/company';
import { BaseService } from './BaseService';

// Загрузка файла может идти долго (большой документ / медленная сеть) — дефолтных 15 с мало.
const UPLOAD_TIMEOUT_MS = 120_000;

interface ContactInfoDto {
  readonly inn?: string;
  readonly ogrn?: string;
  readonly okpo?: string;
  readonly okato?: string;
  readonly okved?: string;
  readonly okvedName?: string;
  readonly kpp?: string;
  readonly kpp2?: string;
  readonly registrationAddress?: string;
  readonly registrationDate?: string;
  readonly site?: string;
  readonly realAddress?: string;
  readonly postAddress?: string;
  readonly belongsToGroup?: string;
  readonly belongsEvidence?: string;
  readonly managementName?: string;
  readonly managementPost?: string;
  readonly contactPhones?: ContactPhone[];
  readonly taxSystems?: TaxSystem[];
}

// Сервис «Моя компания»: карточка организации (user-service read + verification-service save).
class CompanyService extends BaseService {
  /** Карточка организации. GET — идемпотентно. */
  async getCompanyCard(): Promise<CompanyCard> {
    const response = await this.request<ApiEnvelope<ContactInfoDto>>(
      { method: 'get', url: '/personal/contactInfo' },
      { idempotent: true },
    );
    const data = this.unwrap(response.data, response.status);
    return {
      inn: data.inn ?? '',
      ogrn: data.ogrn,
      okpo: data.okpo,
      okato: data.okato,
      okved: data.okved,
      okvedName: data.okvedName,
      kpp: data.kpp,
      registrationAddress: data.registrationAddress,
      registrationDate: data.registrationDate,
      kpp2: data.kpp2 ?? '',
      site: data.site ?? '',
      realAddress: data.realAddress ?? '',
      postAddress: data.postAddress ?? '',
      belongsToGroup: data.belongsToGroup ?? '',
      belongsEvidence: data.belongsEvidence ?? '',
      managementName: data.managementName ?? '',
      managementPost: data.managementPost ?? '',
      contactPhones: (data.contactPhones ?? []).map((contact) => ({
        id: contact.id,
        contactName: contact.contactName ?? '',
        phoneNumber: contact.phoneNumber ?? '',
        isMain: contact.isMain,
      })),
      taxSystems: data.taxSystems ?? [],
    };
  }

  /**
   * Сохранение карточки. Шлём ВЕСЬ объект редактируемых полей (как старый портал) — бэкенд
   * заменяет их целиком, поэтому отправляем актуальные значения всех полей. Мутация — без повтора.
   */
  async saveCompanyCard(payload: CompanyCardSave): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: '/verification/personal/contactInfo', data: payload },
      { retry: false },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Банковские реквизиты ──────────────────────────────────────────────────────

  /** Список счетов организации. */
  async listBankDetails(): Promise<BankAccount[]> {
    const response = await this.request<ApiEnvelope<BankAccount[]>>(
      { method: 'get', url: '/personal/bank_details' },
      { idempotent: true },
    );
    return response.data?.data ?? [];
  }

  /** Данные банка по БИК. null — БИК не найден (невалидный). */
  async getBankByBik(bik: string): Promise<BankInfo | null> {
    try {
      const response = await this.request<ApiEnvelope<BankInfo>>(
        { method: 'get', url: '/personal/bankInfo', params: { bankId: bik } },
        { idempotent: true },
      );
      const data = response.data?.data;
      if (response.data?.success === false || !data?.name) {
        return null;
      }
      return data;
    } catch (error) {
      if (isNetworkError(error)) {
        throw error; // сеть — пробрасываем (покажем тост), а не «невалидный БИК»
      }
      return null;
    }
  }

  /** Проверка расчётного счёта (БИК+Р/с+тип пользователя). true — валиден. */
  async validateRs(bik: string, rs: string, type: string): Promise<boolean> {
    try {
      const response = await this.request<ApiEnvelope<unknown>>(
        { method: 'post', url: '/personal/validate/rs', data: { bik, rs, type } },
        { retry: false },
      );
      return response.data?.success !== false;
    } catch (error) {
      if (isNetworkError(error)) {
        throw error;
      }
      return false;
    }
  }

  /**
   * Сохранение списка счетов (полный список — как старый портал). Удаление = сохранить список без
   * счёта. Бэкенд запрещает пустой список при наличии счетов («нельзя удалить единственный счёт») и
   * отдаёт это как HTTP 200 + success:false — поэтому обязателен ensureSuccess, иначе ошибка проглотится.
   */
  async saveBankDetails(details: BankAccount[]): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: '/personal/bank_details', data: { details } },
      { retry: false },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Аккредитация / документы ──────────────────────────────────────────────────

  /** Все документы аккредитации (группы + флаг сельхозпроизводителя). Ответ без конверта. */
  async getAllDocuments(): Promise<AllDocuments> {
    const response = await this.request<{ item?: AllDocuments['item']; agriculturalProducer?: boolean }>(
      { method: 'get', url: '/verification/all_documents' },
      { idempotent: true },
    );
    return {
      item: response.data?.item ?? [],
      agriculturalProducer: response.data?.agriculturalProducer ?? false,
    };
  }

  /**
   * Загрузка файлов (multipart). onProgress — процент отправленных байт (0..100); как только
   * байты ушли, но ответ ещё не получен, прогресс держится на 100 («завершается»). Таймаут поднят:
   * 15 с (дефолт) мало для файлов на медленной сети. retry:false — повтор POST мог бы задвоить файл.
   */
  async uploadDocuments(
    docType: DocType,
    files: File[],
    onProgress?: (percent: number) => void,
  ): Promise<void> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file, file.name));
    let last = -1;
    const response = await this.request<ApiEnvelope<unknown>>(
      {
        method: 'post',
        url: '/verification/upload/documents',
        params: { docType },
        data: formData,
        onUploadProgress: onProgress
          ? (event) => {
              if (!event.total) {
                return;
              }
              const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
              if (percent !== last) {
                last = percent;
                onProgress(percent);
              }
            }
          : undefined,
      },
      { retry: false, timeoutMs: UPLOAD_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  async deleteDocument(docId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'delete', url: '/verification/delete/document', params: { docId } },
      { retry: false },
    );
    this.ensureSuccess(response.data, response.status);
  }

  async setExpiredDate(docId: number, isPerpetual: boolean, expiredDate?: string): Promise<void> {
    const params = expiredDate ? { docId, isPerpetual, expiredDate } : { docId, isPerpetual };
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: '/verification/set/expired_date', params },
      { retry: false },
    );
    this.ensureSuccess(response.data, response.status);
  }

  async setIsProducer(isProducer: boolean): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: '/verification/set/is_producer', params: { isProducer } },
      { retry: false },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Скачать документ (blob → файл). */
  async downloadDocument(docType: DocType, docId: number): Promise<void> {
    const response = await this.request<Blob>(
      {
        method: 'get',
        url: '/verification/download/document',
        params: { docType, docId },
        responseType: 'blob',
      },
      { idempotent: true },
    );
    const filename =
      filenameFromContentDisposition(response.headers['content-disposition'] as string | undefined) ??
      'document';
    triggerBlobDownload(response.data, filename);
  }

  // ── Агентский договор ─────────────────────────────────────────────────────────

  /** Реквизиты агента площадки. */
  async getAgentInfo(): Promise<AgentInfo> {
    const response = await this.request<ApiEnvelope<AgentInfo>>(
      { method: 'get', url: '/order/agent/info' },
      { idempotent: true },
    );
    return response.data?.data ?? {};
  }

  /** Файлы агентского договора (шаблон + подписанный). Ответ — массив групп без конверта. */
  async getAgencyAgreement(): Promise<DocumentGroup[]> {
    const response = await this.request<DocumentGroup[]>(
      { method: 'get', url: '/verification/agency-agreement' },
      { idempotent: true },
    );
    return Array.isArray(response.data) ? response.data : [];
  }
}

export const companyService = new CompanyService();
